import { create } from 'zustand';
import { RingBuffer } from '../utils';
import { WEBSOCKET_CONFIG } from '../config';

export interface WebSocketMessage {
  id: string;
  channel: string;
  event: string;
  data: unknown;
  timestamp: number;
}

export interface WebSocketMetrics {
  totalMessages: number;
  messagesPerMinute: number;
  reconnectCount: number;
}

interface MetricsState {
  // Metrics
  metrics: WebSocketMetrics;

  // Message history
  messageBuffer: RingBuffer<WebSocketMessage>;
  globalHandlers: Array<(message: WebSocketMessage) => void>;
  maxMessageHistory: number;

  // Message handlers
  addGlobalHandler: (
    handler: (message: WebSocketMessage) => void
  ) => () => void;
  addMessage: (message: WebSocketMessage) => void;

  // Computed getters
  getRecentMessages: (limit: number) => WebSocketMessage[];

  // Metrics updates
  incrementReconnectCount: () => void;
  updateMessagesPerMinute: (count: number) => void;
}

export const useMetricsStore = create<MetricsState>((set, get) => ({
  metrics: {
    totalMessages: 0,
    messagesPerMinute: 0,
    reconnectCount: 0,
  },

  messageBuffer: new RingBuffer<WebSocketMessage>(
    WEBSOCKET_CONFIG.MESSAGES.RING_BUFFER_SIZE
  ),
  globalHandlers: [],
  maxMessageHistory: WEBSOCKET_CONFIG.MESSAGES.MAX_HISTORY_SIZE,

  // Message handlers
  addGlobalHandler: (handler: (message: WebSocketMessage) => void) => {
    set((state) => ({
      globalHandlers: [...state.globalHandlers, handler],
    }));

    // Return unsubscribe function
    return () => {
      set((state) => ({
        globalHandlers: state.globalHandlers.filter((h) => h !== handler),
      }));
    };
  },

  addMessage: (message: WebSocketMessage) => {
    const state = get();

    // Add message to ring buffer
    state.messageBuffer.push(message);

    // Update metrics
    set((state) => ({
      metrics: {
        ...state.metrics,
        totalMessages: state.metrics.totalMessages + 1,
      },
    }));

    // Notify global handlers
    state.globalHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (_error) {
        // Error is already logged by handler
      }
    });
  },

  // Computed getters
  getRecentMessages: (limit: number) => {
    return get().messageBuffer.getRecent(limit);
  },

  // Metrics updates
  incrementReconnectCount: () => {
    set((state) => ({
      metrics: {
        ...state.metrics,
        reconnectCount: state.metrics.reconnectCount + 1,
      },
    }));
  },

  updateMessagesPerMinute: (count: number) => {
    set((state) => ({
      metrics: {
        ...state.metrics,
        messagesPerMinute: count,
      },
    }));
  },
}));
